import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SidebarPage from '../../layout/SidebarPage/SidebarPage';
import PageTitle from '../../layout/PageTitle/PageTitle';
import { Button, Tabs } from 'antd';
import { useGet } from '../../hooks/useApi';
import StudentInformation from './Tabs/StudentInformation';
import { Student as StudentModel } from '../../models/student.model';
import StudentAdditionalGuardians from './Tabs/StudentAdditionalGuardians';
import StudentEmergencyContacts from './Tabs/StudentEmergencyContacts';
import StudentMedicalInformation from './Tabs/StudentMedicalInformation';
import StudentSharingInformation from './Tabs/StudentSharingInformation';
import StudentMedicationList from './Tabs/StudentMedication/List';
import {
  STUDENT_INFO_TABS_CLASS,
  studentInfoTabLabelStyle,
} from './studentInfoTabs';
import './Student.css';

const Student: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<Partial<StudentModel>>({});
  const [activeKey, setActiveKey] = useState('1');

  const tabLabel = (key: string, text: string) => (
    <span
      data-student-info-tab-label={key}
      style={studentInfoTabLabelStyle(activeKey === key)}
    >
      {text}
    </span>
  );

  const api = useGet<any>('/v1/auth/dashboard/student/' + id, {
    enabled: !!id,
  });

  useEffect(() => {
    if (api.isSuccess) {
      setData(api.data);
    }
  }, [api.isSuccess, api.data]);

  const onCreate = (student: any) => {
    navigate(`/student/${student.id}`);
  };

  const OnError = () => (
    <div className={'alert alert-danger'}>
      <table className={'w-100'}>
        <tr>
          <td>Error while loading the page</td>
          <td className={'text-right'} style={{ width: '100px' }}>
            <Button type={'primary'} onClick={() => api.refetch()}>
              Reload
            </Button>
          </td>
        </tr>
      </table>
    </div>
  );

  return (
    <SidebarPage>
      <PageTitle>
        {api.isLoading
          ? 'Loading...'
          : data && data.id
            ? `${data.firstName} ${data.lastName}`
            : `New Student`}
      </PageTitle>

      {id && api.isError && <OnError />}

      {((id && api.isSuccess) || !id) && (
        <Tabs
          activeKey={activeKey}
          onChange={setActiveKey}
          className={STUDENT_INFO_TABS_CLASS}
          items={[
            {
              key: '1',
              label: tabLabel('1', 'Student Information'),
              children: (
                <StudentInformation student={data} created={onCreate} />
              ),
            },
            {
              key: '2',
              label: tabLabel('2', 'Additional Guardians'),
              disabled: !data.id,
              children: <StudentAdditionalGuardians student={data} />,
            },
            {
              key: '3',
              label: tabLabel('3', 'Emergency Contacts'),
              disabled: !data.id,
              children: <StudentEmergencyContacts student={data} />,
            },
            {
              key: '4',
              label: tabLabel('4', 'Medical Information'),
              disabled: !data.id,
              children: <StudentMedicalInformation student={data} />,
            },
            {
              key: '5',
              label: tabLabel('5', 'Medication'),
              disabled: !data.id,
              children: <StudentMedicationList student={data} />,
            },
            {
              key: '6',
              label: tabLabel('6', 'Parent Sharing Information'),
              disabled: !data.id,
              children: <StudentSharingInformation student={data} />,
            },
          ]}
        />
      )}
    </SidebarPage>
  );
};

export default Student;
